import {
  BILLING_PLAN_LABELS,
  BillingPlan,
  SettlementStatus,
} from '@kick/types';
import { AlertTriangle, Calendar, CheckCircle2, Receipt, TrendingUp } from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { useCuentaDelComercio, useMerchantId, type EstadoDeCuenta } from '@/lib/queries';
import { Cargando, ErrorCarga, Vacio } from '@/components/ui';

const MES = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/Bogota',
  });

/**
 * Lo que el comercio le debe a la plataforma.
 *
 * En una sola dirección, y conviene decirlo sin rodeos: el domiciliario le paga
 * el pedido al recogerlo y lo recupera del cliente al entregar, así que el
 * comercio cobra en el momento y no espera dinero de nadie. Lo único pendiente
 * es lo suyo, y esta pantalla existe para que no se entere el día 5.
 */
export function Cuenta() {
  const merchantId = useMerchantId();
  const cuenta = useCuentaDelComercio(merchantId);

  if (cuenta.isLoading) return <Cargando texto="Cargando tu cuenta…" />;
  if (cuenta.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(cuenta.error)} onReintentar={() => void cuenta.refetch()} />
    );
  }

  const { acumulado: a, estados, pendiente } = cuenta.data!;
  const enMensualidad = a.plan === BillingPlan.MONTHLY;
  const vencidos = estados.filter((e) => e.status === SettlementStatus.OVERDUE);

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-3xl text-kick-dark">Mi cuenta</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Lo que le debes a Kick. Tus ventas las cobras tú directamente al domiciliario cuando recoge.
      </p>

      {vencidos.length > 0 && (
        <div className="mt-5 flex items-start gap-3 rounded-kick border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <p className="font-bold text-red-900">Tienes pagos vencidos</p>
            <p className="mt-0.5 text-sm text-red-800">
              Debes {formatCOP(pendiente)}. Mientras no se pague, la tienda queda suspendida y deja
              de recibir pedidos.
            </p>
          </div>
        </div>
      )}

      {/* ── Lo que va del mes ── */}
      <section className="card-kick mt-5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg text-kick-dark">Tu cuenta</h2>
            <p className="text-sm text-kick-gray">
              {a.desde ? `Desde tu último corte (${MES(a.desde)})` : 'Desde tu primer pedido'} ·{' '}
              {BILLING_PLAN_LABELS[a.plan]}
              {!enMensualidad && ` del ${a.commissionRate}%`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-kick-dark">{formatCOP(a.amountDue)}</p>
            <p className="text-xs text-kick-gray">llevas acumulado</p>
          </div>
        </div>

        <dl className="mt-4 space-y-2 border-t border-kick-gray/15 pt-4 text-sm">
          {enMensualidad ? (
            <Linea
              icono={<Calendar className="h-4 w-4" />}
              etiqueta="Mensualidad"
              detalle="Tus pedidos no pagan comisión"
              valor={a.monthlyFee}
            />
          ) : (
            <Linea
              icono={<TrendingUp className="h-4 w-4" />}
              etiqueta={`Comisión del ${a.commissionRate}%`}
              detalle={`${a.ordersCount} ${a.ordersCount === 1 ? 'pedido' : 'pedidos'} por ${formatCOP(a.grossAmount)}`}
              valor={a.commissionAmount}
            />
          )}

        </dl>

        {/*
          La comparación con el otro plan, con el número delante.
          Quien vende mucho ahorra con la mensualidad y quien vende poco al
          revés; decírselo evita que lo tenga que calcular a ojo o que nadie
          se lo diga nunca.
        */}
        {a.conElOtroPlan !== a.amountDue && (
          <p className="mt-4 rounded-kick bg-kick-light p-3 text-sm text-kick-gray">
            {a.conElOtroPlan < a.amountDue ? (
              <>
                Con {enMensualidad ? 'comisión' : 'mensualidad'} este mes pagarías{' '}
                <strong className="text-kick-ink">{formatCOP(a.conElOtroPlan)}</strong>. Habla con
                nosotros si te conviene cambiar.
              </>
            ) : (
              <>
                Con {enMensualidad ? 'comisión' : 'mensualidad'} pagarías{' '}
                <strong className="text-kick-ink">{formatCOP(a.conElOtroPlan)}</strong>: tu plan
                actual te está saliendo mejor.
              </>
            )}
          </p>
        )}

        <p className="mt-3 text-xs text-kick-gray">
          El mes se cierra el último día y tienes los primeros 5 días para pagarlo.
        </p>
      </section>

      {/* ── Lo cerrado ── */}
      <h2 className="mt-8 text-lg text-kick-dark">Meses cerrados</h2>

      {estados.length === 0 ? (
        <div className="mt-3">
          <Vacio
            icono={<Receipt className="h-6 w-6" />}
            titulo="Todavía no hay ninguno"
            descripcion="Al cerrar el primer mes aparecerá aquí, con lo que hay que pagar."
          />
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {estados.map((e) => (
            <FilaEstado key={e.id} estado={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function Linea({
  icono,
  etiqueta,
  detalle,
  valor,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  detalle: string;
  valor: number;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-kick-gray">{icono}</span>
      <div className="min-w-0 flex-1">
        <dt className="font-semibold text-kick-ink">{etiqueta}</dt>
        <dd className="text-xs text-kick-gray">{detalle}</dd>
      </div>
      <span className="font-semibold text-kick-ink">{formatCOP(valor)}</span>
    </div>
  );
}

function FilaEstado({ estado: e }: { estado: EstadoDeCuenta }) {
  const pagado = e.status === SettlementStatus.PAID;
  const vencido = e.status === SettlementStatus.OVERDUE;
  const falta = e.amountDue - e.paidAmount;

  return (
    <article
      className={cn(
        'rounded-kick bg-white p-4 shadow-card',
        vencido && 'border-l-4 border-red-500',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold capitalize text-kick-dark">{MES(e.periodStart)}</p>
          <p className="text-xs text-kick-gray">
            {e.monthlyFee > 0
              ? `Mensualidad ${formatCOP(e.monthlyFee)}`
              : `${e.ordersCount} pedidos · comisión ${formatCOP(e.commissionAmount)}`}

          </p>
          {!!e.dueDate && !pagado && (
            <p className={cn('mt-0.5 text-xs', vencido ? 'font-semibold text-red-700' : 'text-kick-gray')}>
              {vencido ? 'Venció el' : 'Vence el'} {e.dueDate}
            </p>
          )}
        </div>

        <div className="text-right">
          <p className="font-bold text-kick-dark">{formatCOP(e.amountDue)}</p>
          {pagado ? (
            <p className="flex items-center justify-end gap-1 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Pagado
            </p>
          ) : e.paidAmount > 0 ? (
            <p className="text-xs text-kick-gray">Faltan {formatCOP(falta)}</p>
          ) : (
            <p className={cn('text-xs', vencido ? 'font-semibold text-red-700' : 'text-kick-gray')}>
              {vencido ? 'Vencido' : 'Por pagar'}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
